import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useSiteData, usePageContent } from '@/context/SiteDataContext';
import PageHero from '@/components/PageHero';
import { MapPin, ChevronRight, Users } from 'lucide-react';

export function DistrictsList() {
  const c = usePageContent('districts');
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/site/districts').then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <PageHero slug="districts" overline="Regions" heading="Districts" text="The Wisconsin First Jurisdiction is organized into districts, each led by a District Superintendent and District Missionary." />
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map(d => (
            <Link key={d.id} to={`/districts/${d.id}`} data-testid={`district-link-${d.id}`} className="card-tactile overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden">{d.image && <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-royal font-bold group-hover:text-gold transition">{d.name}</h3>
                {d.superintendent && <div className="text-xs text-gold tracking-widest uppercase mt-2">{d.superintendent}</div>}
                <p className="text-charcoal-muted text-sm mt-3">{d.description}</p>
                <div className="mt-4 text-sm text-royal font-semibold flex items-center gap-1">Explore <ChevronRight className="w-4 h-4" /></div>
              </div>
            </Link>
          ))}
          {!items.length && <div className="text-sm text-charcoal-muted">No districts yet.</div>}
        </div>
      </section>
    </div>
  );
}

export function DistrictDetail() {
  const { id } = useParams();
  const [dist, setDist] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { leaders, churches } = useSiteData();
  useEffect(() => { api.get(`/site/districts/${id}`).then(r => setDist(r.data)).catch(() => setNotFound(true)); }, [id]);
  if (notFound) return <div className="py-24 text-center">District not found. <Link to="/districts" className="text-royal underline">Back</Link></div>;
  if (!dist) return <div className="py-24 text-center">Loading…</div>;

  const districtLeaders = (leaders || []).filter(l => l.district_id === dist.id);
  const districtChurches = (churches || []).filter(c => c.district_id === dist.id);

  return (
    <div>
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">{dist.image && <img src={dist.image} alt={dist.name} className="w-full h-full object-cover" />}<div className="absolute inset-0 bg-royal/80" /></div>
        <div className="relative max-w-5xl mx-auto px-6 text-white">
          <div className="overline text-gold mb-4">District</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{dist.name}</h1>
          {dist.description && <p className="mt-4 text-xl text-white/85">{dist.description}</p>}
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="card-tactile p-6">
            <div className="overline mb-2">District Superintendent</div>
            <div className="font-serif text-2xl text-royal font-bold">{dist.superintendent || 'TBA'}</div>
            {dist.superintendent_phone && <div className="text-sm text-charcoal-muted mt-2">☎ {dist.superintendent_phone}</div>}
            {dist.superintendent_email && <div className="text-sm text-charcoal-muted">✉ {dist.superintendent_email}</div>}
          </div>
          <div className="card-tactile p-6">
            <div className="overline mb-2">District Missionary / Chairlady</div>
            <div className="font-serif text-2xl text-royal font-bold">{dist.chairlady || 'TBA'}</div>
            {dist.chairlady_phone && <div className="text-sm text-charcoal-muted mt-2">☎ {dist.chairlady_phone}</div>}
            {dist.chairlady_email && <div className="text-sm text-charcoal-muted">✉ {dist.chairlady_email}</div>}
          </div>
        </div>
      </section>

      {districtLeaders.length > 0 && (
        <section className="bg-[#F8F9FA] py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="overline mb-3">District Leadership</div>
            <h2 className="font-serif text-3xl text-royal font-bold mb-8 gold-underline">Leaders in this District</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {districtLeaders.map(l => (
                <div key={l.id} className="card-tactile overflow-hidden">
                  <div className="aspect-square overflow-hidden" style={{ backgroundColor: '#F1E9D6' }}>
                    {l.photo && <img src={l.photo} alt={l.name} className="w-full h-full object-cover" style={{ objectPosition: l.photoPosition || 'center top', transform: l.photoScale ? `scale(${l.photoScale})` : undefined, transformOrigin: 'center top' }} />}
                  </div>
                  <div className="p-4">
                    <div className="text-xs text-gold uppercase tracking-widest">{l.role}</div>
                    <div className="font-serif text-lg text-royal font-bold mt-1">{l.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {districtChurches.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="overline mb-3">Churches</div>
            <h2 className="font-serif text-3xl text-royal font-bold mb-8 gold-underline">Congregations in {dist.name}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {districtChurches.map(c => (
                <div key={c.id} className="card-tactile p-6">
                  <div className="font-serif text-xl text-royal font-bold">{c.name}</div>
                  <div className="text-sm text-gold mt-1">{c.pastor}</div>
                  <div className="mt-3 text-sm text-charcoal-muted flex items-start gap-2"><MapPin className="w-4 h-4 text-royal mt-0.5" /> {c.address}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
